import React, { useRef, useState } from 'react';
import { checkUnmeContractPayoutResult, getAdminCpSettlementDetail } from '../api/cpSettlement';
import { hasUnmappedUnmeContract } from '../api/unmePayout';
import { unmeContractColor } from '../../Common/utils/unmeContractColor';

// 한 화면에서 동일 정산의 여러 로그가 열려도 동일 지급 시도 조회는 중복하지 않는다.
const checkingPayoutIds = new Set();

const money = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number' && (!Number.isFinite(value) || Math.abs(value) > Number.MAX_SAFE_INTEGER)) return '—';
  const raw = String(value).replace(/,/g, '').trim();
  if (!/^-?\d+(?:\.\d+)?$/.test(raw)) return '—';
  const [integer, fraction] = raw.split('.');
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const significantFraction = fraction?.replace(/0+$/, '');
  return `${grouped}${significantFraction ? `.${significantFraction}` : ''}원`;
};

const payoutStatus = (status) => ({ SENDING: '지급 요청 처리 중', SUCCESS: '지급 완료', DECLINED: '지급 실패', UNKNOWN: '지급 결과 확인 필요' }[String(status || '').toUpperCase()] || '상태 확인 필요');
const contractLabel = (contract) => contract.contractKey === null || (contract.contractKey === undefined && contract.contract_key === null) ? '-' : (contract.contractName || contract.contract_name || (contract.service_id != null ? `서비스 ${contract.service_id}` : '서비스 확인 필요'));
const contractBadgeColor = (contract) => unmeContractColor(contractLabel(contract) === '-' || !(contract.contractName || contract.contract_name) ? null : (contract.contractKey ?? contract.contract_key));
const isNonPositiveAmount = (value) => {
  if (value == null) return false;
  const raw = String(value).replace(/,/g, '').trim();
  return /^-\d+(?:\.\d+)?$/.test(raw) || /^0+(?:\.0+)?$/.test(raw);
};
const newestFirst = (a, b) => Number(b.attempt_no ?? -1) - Number(a.attempt_no ?? -1);
const fields = [
  ['승인금액', 'gross_amount'], ['수수료', 'fee_amount'], ['PG 수수료', 'pg_fee_amount'],
  ['본사 수수료', 'admin_fee_amount'], ['취소금액', 'cancel_amount'], ['취소 수수료 환급', 'canceled_fee_refund_amount'],
  ['이월금 유입', 'carry_in'], ['이월금 유출', 'carry_out'],
];

export default function UnmeContractSettlement({ settlementId, adminRole = '', onResultChecked }) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState({ loading: false, data: null, error: '' });
  const [expanded, setExpanded] = useState(false);
  const [checking, setChecking] = useState(false);
  const [resultNotice, setResultNotice] = useState('');
  const checkingRef = useRef(false);
  const validId = /^\d+$/.test(String(settlementId ?? '')) && Number(settlementId) > 0;

  const load = async () => {
    setState({ loading: true, data: null, error: '' });
    try {
      const data = await getAdminCpSettlementDetail(settlementId);
      setState({ loading: false, data, error: '' });
    } catch (_) {
      setState({ loading: false, data: null, error: '계약별 내역을 불러오지 못했습니다.' });
    }
  };

  const checkResult = async (payout) => {
    const payoutId = String(payout?.id ?? '');
    if (adminRole !== 'SUPER' || !/^\d+$/.test(payoutId) || Number(payoutId) <= 0 || checkingRef.current || checkingPayoutIds.has(payoutId)) return;
    checkingRef.current = true;
    checkingPayoutIds.add(payoutId);
    setChecking(true);
    setResultNotice('');
    try {
      await checkUnmeContractPayoutResult(payoutId);
      setResultNotice('조회 요청을 완료했습니다. 최신 지급 상태를 확인해 주세요.');
    } catch (_) {
      setResultNotice('지급 결과를 확인하지 못했습니다. 최신 내역을 확인하거나 다시 조회해 주세요.');
    } finally {
      await load();
      if (onResultChecked) onResultChecked();
      checkingPayoutIds.delete(payoutId);
      checkingRef.current = false;
      setChecking(false);
    }
  };

  if (!validId) return null;
  const contractData = state.data?.contractSettlement;
  const allContracts = Array.isArray(contractData?.contracts) ? contractData.contracts : [];
  const contracts = allContracts.filter((contract) => !hasUnmappedUnmeContract(contract));
  const hasUnmapped = hasUnmappedUnmeContract(contractData);
  const payouts = Array.isArray(contractData?.payouts) ? contractData.payouts : [];
  const contractIds = new Set(allContracts.filter((contract) => contract.id != null).map((contract) => String(contract.id)));
  const unmatchedPayouts = payouts.filter((payout) => payout.contract_id == null || !contractIds.has(String(payout.contract_id)));
  const attemptsFor = (contract) => contract.id == null ? [] : payouts.filter((payout) => payout.contract_id != null && String(payout.contract_id) === String(contract.id)).sort(newestFirst);
  const statusFor = (contract, attempts) => attempts.length ? payoutStatus(attempts[0].status) : contract.id == null ? '계약 확인 필요' : !Array.isArray(contractData?.payouts) ? '지급 이력 확인 필요' : money(contract.net_amount) === '—' ? '지급 금액 확인 필요' : isNonPositiveAmount(contract.net_amount) ? '지급 대상 없음' : '지급 요청 전';
  const payoutHistory = (attempts) => attempts.map((payout, index) => <div key={payout.id ?? index} className="border-t border-slate-100 py-1">
    <div className="flex justify-between gap-2"><span>계약 {payout.contract_id ?? '확인 필요'} · {payout.attempt_no ?? '—'}회 · {payoutStatus(payout.status)}</span><strong>{money(payout.amount)}</strong></div>
    {(payout.response_code || payout.transaction_id) && <p className="break-all text-slate-500">{[payout.response_code, payout.transaction_id].filter(Boolean).join(' · ')}</p>}
  </div>);
  const parentStatus = String(state.data?.settlement?.status || '').toUpperCase();
  return (
    <div className="mt-2 border-t border-slate-200 pt-2 text-xs" onClick={(event) => event.stopPropagation()}>
      <button type="button" onClick={() => { if (!open) load(); setOpen(!open); }} className="rounded border border-slate-300 bg-white px-2 py-1 font-semibold text-slate-700 hover:bg-slate-50" aria-expanded={open}>
        {open ? '계약별 내역 접기' : '계약별 내역 보기'}
      </button>
      {open && <div className="mt-2 space-y-2">
        {state.loading && <p className="text-slate-500">계약별 내역을 불러오는 중입니다.</p>}
        {state.error && <p className="text-red-600">{state.error} <button type="button" onClick={load} className="underline">다시 조회</button></p>}
        {state.data && contractData === null && <p className="text-slate-500">-</p>}
        {state.data && contractData === undefined && <p className="text-slate-500">계약별 정보가 제공되지 않았습니다.</p>}
        {hasUnmapped && <p role="status" className="text-amber-700">계약 정보 확인 필요</p>}
        {contractData != null && !Array.isArray(contractData.contracts) && <p className="text-amber-700">계약별 집계를 확인할 수 없습니다.</p>}
        {contractData != null && Array.isArray(contractData.contracts) && allContracts.length === 0 && <p className="text-amber-700">계약별 집계 확인 필요</p>}
        {contracts.length > 0 && <>
          <p className="font-semibold text-slate-600">전체 지급: {parentStatus === 'TRANSFERRED' ? '완료' : parentStatus ? '완료 전' : '상태 확인 필요'}</p>
          {contracts.map((contract, index) => {
            const attempts = attemptsFor(contract);
            const latest = attempts[0];
            const canCheck = adminRole === 'SUPER' && ['UNKNOWN', 'SENDING'].includes(String(latest?.status || '').toUpperCase()) && /^\d+$/.test(String(latest?.id ?? '')) && Number(latest.id) > 0;
            return <div key={`${contract.id ?? 'unknown'}-${index}`} className="flex flex-wrap items-center justify-between gap-2 rounded bg-white px-2 py-1.5">
              <span><span className={`inline-flex rounded border px-1.5 py-0.5 font-semibold ${contractBadgeColor(contract)}`}>{contractLabel(contract)}</span> · {statusFor(contract, attempts)}</span><span className="flex items-center gap-2"><strong>{money(contract.net_amount)}</strong>{canCheck && <button type="button" disabled={checking} onClick={() => checkResult(latest)} className="rounded border border-slate-300 px-2 py-1 font-semibold text-slate-700 disabled:opacity-50" aria-label={`${contractLabel(contract)} 지급 결과 다시 확인`}>{checking ? '조회 중…' : '결과 다시 확인'}</button>}</span>
            </div>;
          })}
          {resultNotice && <p role="status" className="text-slate-600">{resultNotice}</p>}
          <button type="button" onClick={() => setExpanded(!expanded)} className="font-semibold text-blue-700 underline" aria-expanded={expanded}>{expanded ? '산출 및 지급 이력 접기' : '산출 및 지급 이력 보기'}</button>
          {expanded && <div className="space-y-2">
            {contracts.map((contract, index) => <div key={`${contract.id ?? 'unknown'}-${index}`} className="rounded border border-slate-200 bg-white p-2">
              <p className="mb-1"><span className={`inline-flex rounded border px-1.5 py-0.5 font-semibold ${contractBadgeColor(contract)}`}>{contractLabel(contract)}</span></p>
              {fields.map(([label, key]) => <div className="flex justify-between gap-2 py-0.5" key={key}><span className="text-slate-500">{label}</span><span>{money(contract[key])}</span></div>)}
              <p className="mb-1 mt-2 font-bold">지급 시도 이력</p>
              {attemptsFor(contract).length ? payoutHistory(attemptsFor(contract)) : <p className="text-slate-500">{contract.id == null || !Array.isArray(contractData.payouts) ? '지급 시도 이력을 확인할 수 없습니다.' : '지급 시도 이력이 없습니다.'}</p>}
            </div>)}
            {!Array.isArray(contractData.payouts) && <p className="text-slate-500">지급 시도 이력이 제공되지 않았습니다.</p>}
          </div>}
        </>}
        {unmatchedPayouts.length > 0 && <div className="rounded border border-amber-200 bg-white p-2"><p className="mb-1 font-bold text-amber-700">계약을 확인할 수 없는 지급 이력</p>{payoutHistory(unmatchedPayouts)}</div>}
      </div>}
    </div>
  );
}
